import React, { useState, useEffect, useMemo } from 'react';
import {
    Box, Typography, Alert, Collapse, Stack, IconButton, Button,
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { alpha } from '@mui/material/styles';
import { fetchAndMergeCalendarData } from '../utils/dataUtils';
import { fetchAndProcessData } from '../utils/hockeyStatsParser';
import { CURRENT_SEASON } from '../seasons';
import { CalendarEvent } from '../types';
import { useRoster } from '../RosterContext';
import { useSeason } from '../SeasonContext';
import { normalizeGoogleSheetEditUrl } from '../utils/sheetUtils';
import {
    ScheduleConflict,
    DiscrepancyConflict,
    findScheduleConflicts,
    getOpponentFromGame,
    getOpponentFromSummary,
    getLeagueGameTime,
} from '../utils/scheduleConflictUtils';

interface ScheduleConflictDisplayProps {
    calendarEvents?: CalendarEvent[];
    isLoadingEvents?: boolean;
    filter?: 'all' | 'calendar' | 'volunteers';
    volunteerSheetUrl?: string;
}

const DiscrepancyDetails: React.FC<{ conflict: DiscrepancyConflict }> = ({ conflict }) => {
    const leagueTime = getLeagueGameTime(conflict.leagueGame).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
    const calendarTime = new Date(conflict.calendarGame.start).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
    return (
        <Box>
            <Typography variant="body2" component="div" gutterBottom>
                <strong>Game vs {getOpponentFromGame(conflict.leagueGame)} on {getLeagueGameTime(conflict.leagueGame).toLocaleDateString()}:</strong>
            </Typography>
            <TableContainer component={Paper} elevation={0} sx={theme => ({
                my: 1,
                borderRadius: 2,
                bgcolor: alpha(theme.palette.warning.main, 0.06)
            })}>
                <Table size="small" aria-label="discrepancy details" sx={{ '& th, & td': { border: 0 } }}>
                    <TableHead>
                        <TableRow>
                            <TableCell sx={{ fontWeight: 'bold' }}></TableCell>
                            <TableCell sx={{ fontWeight: 'bold' }}>League Schedule</TableCell>
                            <TableCell sx={{ fontWeight: 'bold' }}>Team Calendar</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {conflict.timeMismatch && (
                            <TableRow>
                                <TableCell component="th" scope="row" sx={{ fontWeight: 'medium' }}>Time</TableCell>
                                <TableCell>{leagueTime}</TableCell>
                                <TableCell>{calendarTime}</TableCell>
                            </TableRow>
                        )}
                        {conflict.locationMismatch && (
                            <TableRow>
                                <TableCell component="th" scope="row" sx={{ fontWeight: 'medium' }}>Location</TableCell>
                                <TableCell>{conflict.leagueGame.location}</TableCell>
                                <TableCell>{conflict.calendarGame.location}</TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
            </TableContainer>
        </Box>
    );
};

const ScheduleConflictDisplay: React.FC<ScheduleConflictDisplayProps> = ({ 
    calendarEvents: passedEvents,
    isLoadingEvents,
    filter = 'all',
    volunteerSheetUrl,
}) => {
    const { isAuthReady, isTeamManager } = useRoster();
    const { currentSeason, selectedSeason } = useSeason();
    const effectiveSheetUrl = useMemo(
        () => normalizeGoogleSheetEditUrl(volunteerSheetUrl || selectedSeason?.volunteerSheetUrl || currentSeason?.volunteerSheetUrl),
        [volunteerSheetUrl, selectedSeason, currentSeason]
    );
    const [conflicts, setConflicts] = useState<ScheduleConflict[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [expanded, setExpanded] = useState(true);

    useEffect(() => {
        let isMounted = true;

        if (isLoadingEvents) {
            setLoading(true);
            return;
        }

        const runCheck = async () => {
            if (passedEvents === undefined && !isAuthReady) {
                setLoading(true);
                return;
            }

            try {
                const [calendarEvents, statsData] = await Promise.all([
                    passedEvents !== undefined ? Promise.resolve(passedEvents) : fetchAndMergeCalendarData(),
                    fetchAndProcessData(CURRENT_SEASON.seasonId, CURRENT_SEASON.divisionId)
                ]);

                if (!isMounted) return;

                if (passedEvents !== undefined && passedEvents.length === 0 && statsData.fullSchedule.length > 0) {
                    setLoading(true);
                    return;
                }

                const leagueTeamNames = new Set(statsData.standings.map(team => team.name));
                const foundConflicts = findScheduleConflicts(calendarEvents, statsData.fullSchedule, leagueTeamNames);

                setConflicts(foundConflicts);
                setExpanded(foundConflicts.length <= 3);
                setError(null);
            } catch (e: any) {
                if (isMounted) {
                    setError(e.message);
                    console.error("Failed to check for schedule conflicts:", e);
                }
            } finally {
                if (isMounted) {
                    setLoading(false);
                }
            }
        };

        runCheck();

        return () => {
            isMounted = false;
        };
    }, [passedEvents, isLoadingEvents, isAuthReady]);

    const filteredConflicts = useMemo(() => {
        if (filter === 'calendar') {
            return conflicts.filter(c => c.type !== 'missing_volunteers');
        }
        if (filter === 'volunteers') {
            return conflicts.filter(c => c.type === 'missing_volunteers');
        }
        return conflicts;
    }, [conflicts, filter]);

    const hasContent = !loading && (filteredConflicts.length > 0 || !!error);

    const bannerTitle = filter === 'volunteers'
        ? "Missing Volunteers Detected!"
        : "Schedule Discrepancies Found!";

    return (
        <Collapse in={hasContent} timeout="auto" unmountOnExit>
            {error ? (
                <Alert severity="error" sx={{ mb: 3 }}>Could not check for schedule conflicts: {error}</Alert>
            ) : (
                <Alert
                    severity="warning"
                    sx={{
                        mb: 3,
                        '& .MuiAlert-message': { width: '100%' },
                        borderRadius: 2,
                    }}
                >
                    <Box
                        onClick={() => setExpanded(prev => !prev)}
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            cursor: 'pointer',
                            userSelect: 'none',
                        }}
                    >
                        <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap" sx={{ gap: 1 }}>
                            <Typography fontWeight="bold">{bannerTitle}</Typography>
                            {filter === 'volunteers' && isTeamManager && effectiveSheetUrl && (
                                <Button
                                    size="small"
                                    variant="outlined"
                                    color="inherit"
                                    href={effectiveSheetUrl}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    startIcon={<OpenInNewIcon fontSize="small" />}
                                    onClick={(e) => e.stopPropagation()}
                                    sx={{ py: 0.25, px: 1, fontSize: '0.75rem', textTransform: 'none' }}
                                >
                                    Edit Sheet
                                </Button>
                            )}
                        </Stack>
                        <IconButton
                            size="small"
                            onClick={(e) => {
                                e.stopPropagation();
                                setExpanded(prev => !prev);
                            }}
                            aria-label={expanded ? "collapse discrepancies" : "expand discrepancies"}
                            sx={{ p: 0.25 }}
                        >
                            {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                        </IconButton>
                    </Box>
                    <Collapse in={expanded}>
                        <Stack spacing={2} sx={{ mt: 2 }}>
                            {filteredConflicts.map((conflict, index) => (
                                <Box key={index}>
                                    {conflict.type === 'discrepancy' && <DiscrepancyDetails conflict={conflict} />}
                                    {conflict.type === 'missing_from_calendar' && (
                                        <Typography variant="body2"><strong>Missing Game:</strong> Game vs {getOpponentFromGame(conflict.leagueGame)} on {getLeagueGameTime(conflict.leagueGame).toLocaleDateString()} is on the league schedule but not found on the team calendar.</Typography>
                                    )}
                                    {conflict.type === 'missing_from_league' && (
                                        <Typography variant="body2"><strong>Extra Game:</strong> Game vs {getOpponentFromSummary(conflict.calendarGame.summary)} on {new Date(conflict.calendarGame.start).toLocaleDateString()} is on the team calendar but not found on the league schedule.</Typography>
                                    )}
                                    {conflict.type === 'missing_volunteers' && (
                                        <Typography variant="body2"><strong>Missing Volunteers:</strong> The game vs {getOpponentFromSummary(conflict.calendarGame.summary) || conflict.calendarGame.summary} on {new Date(conflict.calendarGame.start).toLocaleDateString()} is missing {conflict.missingDuties && conflict.missingDuties.length > 0 ? conflict.missingDuties.join(' and ') : 'volunteer'} duties.</Typography>
                                    )}
                                </Box>
                            ))}
                        </Stack>
                    </Collapse>
                </Alert>
            )}
        </Collapse>
    );
};

export default ScheduleConflictDisplay;
